import { CallToActionType, CampaignType } from "common/interfaces/KYDMarketing";

export interface CallToActionOption {
  label: string;
  value: CallToActionType | null;
}

export type PlacementType = "website";

/**
 * Converts a CallToActionType enum value to a human-readable label
 */
function formatCallToActionLabel(type: CallToActionType): string {
  return type
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Set of call-to-action types available for awareness campaigns
 * Based on Meta's available CTAs for Awareness objective
 */
const AWARENESS_CAMPAIGN_CTAS: Set<CallToActionType> = new Set([
  CallToActionType.APPLY_NOW,
  CallToActionType.BOOK_NOW,
  CallToActionType.CALL_NOW,
  CallToActionType.CONTACT_US,
  CallToActionType.DOWNLOAD,
  CallToActionType.GET_DIRECTIONS,
  CallToActionType.GET_QUOTE,
  CallToActionType.GET_SHOWTIMES,
  CallToActionType.LEARN_MORE,
  CallToActionType.LISTEN_NOW,
  CallToActionType.ORDER_NOW,
  CallToActionType.REQUEST_TIME,
  CallToActionType.MESSAGE_PAGE, // Send message
  CallToActionType.WHATSAPP_MESSAGE, // Send WhatsApp message
  CallToActionType.SHOP_NOW,
  CallToActionType.SIGN_UP,
  CallToActionType.SUBSCRIBE,
  CallToActionType.WATCH_MORE,
]);

/**
 * Set of call-to-action types available for conversion campaigns with Website conversion location
 * Based on Meta's available CTAs for Leads objective with Website conversion location
 */
const CONVERSION_WEBSITE_CTAS: Set<CallToActionType> = new Set([
  CallToActionType.APPLY_NOW,
  CallToActionType.BOOK_NOW,
  CallToActionType.CONTACT_US,
  CallToActionType.DONATE_NOW,
  CallToActionType.DOWNLOAD,
  CallToActionType.GET_OFFER,
  CallToActionType.GET_QUOTE,
  CallToActionType.GET_SHOWTIMES,
  CallToActionType.LEARN_MORE,
  CallToActionType.LISTEN_NOW,
  CallToActionType.ORDER_NOW,
  CallToActionType.PLAY_GAME,
  CallToActionType.REQUEST_TIME,
  CallToActionType.SHOP_NOW,
  CallToActionType.SIGN_UP,
  CallToActionType.SUBSCRIBE,
  CallToActionType.WATCH_MORE,
]);

/**
 * Generates call-to-action options for frontend forms
 *
 * @example
 * // Get all call-to-action options for an awareness campaign
 * const awarenessOptions = generateCallToActionOptions(CampaignType.AWARENESS);
 *
 * // Get all call-to-action options for a conversion campaign (Website conversion location)
 * const conversionOptions = generateCallToActionOptions(CampaignType.CONVERSION);
 *
 * @param campaignType - The type of campaign (awareness or conversion/sales)
 * @param includeEmptyOption - Whether to include an empty "Select a call to action" option
 * @param placementType - Deprecated: No longer used, kept for backward compatibility
 * @returns Array of CallToActionOption objects
 */
export function generateCallToActionOptions(
  campaignType: CampaignType,
  includeEmptyOption: boolean = true,
  placementType?: PlacementType
): CallToActionOption[] {
  const options: CallToActionOption[] = [];

  if (includeEmptyOption) {
    options.push({
      label: "Select a call to action",
      value: null,
    });
  }

  let ctaTypes = Object.values(CallToActionType);

  // Filter CTAs based on campaign type
  if (campaignType === CampaignType.AWARENESS) {
    // For awareness campaigns, only show CTAs that are available for awareness objective
    ctaTypes = ctaTypes.filter((cta) => AWARENESS_CAMPAIGN_CTAS.has(cta));
  } else if (campaignType === CampaignType.CONVERSION) {
    // For conversion campaigns, only show CTAs available for Website conversion location
    ctaTypes = ctaTypes.filter((cta) => CONVERSION_WEBSITE_CTAS.has(cta));
  }

  const sortedTypes = ctaTypes.sort((a, b) => {
    const labelA = formatCallToActionLabel(a);
    const labelB = formatCallToActionLabel(b);
    return labelA.localeCompare(labelB);
  });

  sortedTypes.forEach((type) => {
    options.push({
      label: formatCallToActionLabel(type),
      value: type,
    });
  });

  return options;
}
